import { useCallback, useMemo, useState } from 'react';
import { Badge, Button, Card, Input, Select } from '@nexora/design-system';
import {
  OPERATION_TYPE_LABEL,
  REGIME_LABEL,
  TAX_DETAIL_KEY,
  TAX_KEYS,
  TAX_LABEL,
  baseTotal,
  effectiveRate,
  itemTaxValue,
  useTaxCalculation,
  type CalculateItemInput,
  type CalculateTaxesInput,
  type CalculateTaxesResponse,
  type CalculatedItemDetail,
  type OperationType,
  type TaxKey,
  type TaxRegimeCode,
} from './useTaxCalculate';
import { formatCurrency } from '../../../shared/utils/format';

/* ── Vocabulario do motor ──────────────────────────────────────────────────
 * Operacao e regime saem de `OperationType` e `TaxRegimeCode`. A tela antiga
 * oferecia "DEVOLUCAO" (o motor tem DEVOLUCAO_VENDA e DEVOLUCAO_COMPRA) e
 * escondia SIMPLES_NACIONAL_EXCESSO e MEI, que o calculo trata.
 */
const OPERATION_TYPES = Object.keys(OPERATION_TYPE_LABEL) as OperationType[];
const REGIMES = Object.keys(REGIME_LABEL) as TaxRegimeCode[];

const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE',
  'TO', 'EX',
] as const;

/** `EX` e o que o motor le como exportacao (`TaxCalculationContext.isExportacao`). */
const ufLabel = (uf: string): string => (uf === 'EX' ? 'EX (exterior)' : uf);

/* ── Parametros de um cenario ────────────────────────────────────────────── */

interface ScenarioParams {
  operationType: OperationType;
  originUf: string;
  destinationUf: string;
  emitterRegime: TaxRegimeCode;
  destinationRegime: TaxRegimeCode;
  /** `YYYY-MM-DD`. Decide vigencia de aliquota e fase da reforma. */
  date: string;
}

interface ItemRow {
  key: string;
  ncmCode: string;
  quantity: string;
  unitValue: string;
}

let proximaChave = 0;
function novoItem(ncm = '', quantidade = '1', valor = ''): ItemRow {
  proximaChave += 1;
  return { key: `item-${proximaChave}`, ncmCode: ncm, quantity: quantidade, unitValue: valor };
}

function hoje(): string {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/**
 * Mesma data, ano seguinte.
 *
 * E o cenario B mais util de ja vir preenchido: a data escolhe a aliquota
 * vigente e, de 2026 em diante, liga IBS/CBS com o percentual de transicao
 * do ano (`TaxCalculationContext.isReformaTributaria`).
 */
function anoSeguinte(iso: string): string {
  return `${Number(iso.slice(0, 4)) + 1}${iso.slice(4)}`;
}

/** `YYYY-MM-DD` para `DD/MM/YYYY`, sem passar por `Date` (que interpreta em UTC). */
function dataCivil(iso: string): string {
  const [ano, mes, dia] = iso.split('-');
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : iso;
}

const PARAMS_PADRAO: ScenarioParams = {
  operationType: 'VENDA',
  originUf: 'SP',
  destinationUf: 'RJ',
  emitterRegime: 'LUCRO_PRESUMIDO',
  destinationRegime: 'LUCRO_PRESUMIDO',
  date: hoje(),
};

/** Itens que o motor consegue calcular: NCM preenchido, quantidade e valor positivos. */
function itensValidos(rows: ItemRow[]): CalculateItemInput[] {
  const validos: CalculateItemInput[] = [];
  for (const row of rows) {
    const ncm = row.ncmCode.replace(/\D/g, '');
    const quantidade = Number(row.quantity.replace(',', '.'));
    const valor = Number(row.unitValue.replace(',', '.'));
    if (ncm.length < 4 || !(quantidade > 0) || !(valor > 0)) continue;
    validos.push({ ncmCode: ncm, quantity: quantidade, unitValue: valor });
  }
  return validos;
}

function montarPedido(params: ScenarioParams, items: CalculateItemInput[]): CalculateTaxesInput | null {
  if (items.length === 0) return null;
  return {
    operationType: params.operationType,
    originUf: params.originUf,
    destinationUf: params.destinationUf,
    emitterRegime: params.emitterRegime,
    destinationRegime: params.destinationRegime,
    items,
    date: params.date,
  };
}

/** Em que os dois cenarios diferem — sem isso o comparativo nao explica o delta. */
function diferencas(a: ScenarioParams, b: ScenarioParams): string[] {
  const lista: string[] = [];
  if (a.operationType !== b.operationType) {
    lista.push(`operação: ${OPERATION_TYPE_LABEL[a.operationType]} → ${OPERATION_TYPE_LABEL[b.operationType]}`);
  }
  if (a.originUf !== b.originUf) lista.push(`UF de origem: ${a.originUf} → ${b.originUf}`);
  if (a.destinationUf !== b.destinationUf) lista.push(`UF de destino: ${a.destinationUf} → ${b.destinationUf}`);
  if (a.emitterRegime !== b.emitterRegime) {
    lista.push(`regime do emitente: ${REGIME_LABEL[a.emitterRegime]} → ${REGIME_LABEL[b.emitterRegime]}`);
  }
  if (a.destinationRegime !== b.destinationRegime) {
    lista.push(`regime do destinatário: ${REGIME_LABEL[a.destinationRegime]} → ${REGIME_LABEL[b.destinationRegime]}`);
  }
  if (a.date !== b.date) lista.push(`data da operação: ${dataCivil(a.date)} → ${dataCivil(b.date)}`);
  return lista;
}

/** Sem o BOM, o Excel em pt-BR abre o CSV como ANSI e quebra os acentos. */
const BOM_UTF8 = String.fromCharCode(0xfeff);

function formatPercent(valor: number | null, digitos = 2): string {
  if (valor === null || !Number.isFinite(valor)) return '—';
  return `${valor.toFixed(digitos).replace('.', ',')}%`;
}

function formatDelta(valor: number): string {
  return `${valor > 0 ? '+' : ''}${formatCurrency(valor)}`;
}

/** Tributo a mais e vermelho; a menos, verde. Diferenca de centavo e neutra. */
function deltaClass(valor: number): string {
  if (Math.abs(valor) < 0.005) return 'sim-delta sim-delta--flat';
  return valor > 0 ? 'sim-delta sim-delta--up' : 'sim-delta sim-delta--down';
}

/* ── Painel de parametros de um cenario ───────────────────────────────────── */

interface ScenarioFormProps {
  titulo: string;
  etiqueta: string;
  params: ScenarioParams;
  onChange: (params: ScenarioParams) => void;
  resultado: CalculateTaxesResponse | undefined;
  carregando: boolean;
}

function ScenarioForm({ titulo, etiqueta, params, onChange, resultado, carregando }: ScenarioFormProps): JSX.Element {
  function set<K extends keyof ScenarioParams>(campo: K, valor: ScenarioParams[K]): void {
    onChange({ ...params, [campo]: valor });
  }

  const regrasAvaliadas = resultado?.auditTrail.totalRulesEvaluated ?? 0;
  const plural = regrasAvaliadas === 1 ? '' : 's';

  return (
    <div className="sim-scenario">
      <div className="sim-scenario__head">
        <span className="sim-scenario__tag">{etiqueta}</span>
        <h3 className="sim-scenario__title">{titulo}</h3>
      </div>

      <div className="sim-grid">
        <Select
          label="Operação"
          value={params.operationType}
          onChange={(e) => set('operationType', e.target.value as OperationType)}
        >
          {OPERATION_TYPES.map((op) => (
            <option key={op} value={op}>
              {OPERATION_TYPE_LABEL[op]}
            </option>
          ))}
        </Select>
        <Input
          label="Data da operação"
          type="date"
          value={params.date}
          onChange={(e) => set('date', e.target.value)}
        />
        <Select label="UF de origem" value={params.originUf} onChange={(e) => set('originUf', e.target.value)}>
          {UFS.map((uf) => (
            <option key={uf} value={uf}>
              {ufLabel(uf)}
            </option>
          ))}
        </Select>
        <Select
          label="UF de destino"
          value={params.destinationUf}
          onChange={(e) => set('destinationUf', e.target.value)}
        >
          {UFS.map((uf) => (
            <option key={uf} value={uf}>
              {ufLabel(uf)}
            </option>
          ))}
        </Select>
        <Select
          label="Regime do emitente"
          value={params.emitterRegime}
          onChange={(e) => set('emitterRegime', e.target.value as TaxRegimeCode)}
        >
          {REGIMES.map((r) => (
            <option key={r} value={r}>
              {REGIME_LABEL[r]}
            </option>
          ))}
        </Select>
        <Select
          label="Regime do destinatário"
          value={params.destinationRegime}
          onChange={(e) => set('destinationRegime', e.target.value as TaxRegimeCode)}
        >
          {REGIMES.map((r) => (
            <option key={r} value={r}>
              {REGIME_LABEL[r]}
            </option>
          ))}
        </Select>
      </div>

      {/* A regra que casou e quantas foram avaliadas: e o motor dizendo por que
          o numero deu isso. Sem este bloco, o delta e um valor sem causa. */}
      <div className="sim-rule">
        {carregando && !resultado ? (
          <span className="sim-muted">calculando…</span>
        ) : resultado ? (
          <>
            <span className="sim-rule__label">Regra aplicada</span>
            {resultado.ruleApplied ? (
              <strong className="sim-rule__name" title={resultado.ruleApplied.id}>
                {resultado.ruleApplied.name}
              </strong>
            ) : (
              <Badge variant="warning" size="sm">
                nenhuma regra casou
              </Badge>
            )}
            <span className="sim-muted">
              {regrasAvaliadas} regra{plural} avaliada{plural}
              {resultado.cfopResolved.code ? ` · CFOP ${resultado.cfopResolved.code}` : ''}
            </span>
          </>
        ) : (
          <span className="sim-muted">informe ao menos um item</span>
        )}
      </div>
    </div>
  );
}

/* ── Impacto por tributo ──────────────────────────────────────────────────── */

interface LinhaTributo {
  key: TaxKey;
  label: string;
  a: number;
  b: number;
}

function ImpactChart({ linhas }: { linhas: LinhaTributo[] }): JSX.Element {
  const maior = Math.max(...linhas.map((l) => Math.max(l.a, l.b)), 1);

  return (
    <div className="sim-chart">
      <div className="sim-chart__row sim-chart__row--head">
        <span className="sim-chart__label" />
        <div className="sim-chart__legend">
          <span className="sim-chart__key">
            <i className="sim-swatch sim-swatch--a" /> Cenário A
          </span>
          <span className="sim-chart__key">
            <i className="sim-swatch sim-swatch--b" /> Cenário B
          </span>
        </div>
        <span className="sim-chart__values">
          <span className="sim-chart__col">A</span>
          <span className="sim-chart__col">B</span>
          <span className="sim-chart__col">Δ (B−A)</span>
        </span>
      </div>
      {linhas.map((linha) => (
        <div key={linha.key} className="sim-chart__row">
          <span className="sim-chart__label">{linha.label}</span>
          <div className="sim-chart__bars">
            <div className="sim-chart__bar sim-chart__bar--a" style={{ width: `${(linha.a / maior) * 100}%` }} />
            <div className="sim-chart__bar sim-chart__bar--b" style={{ width: `${(linha.b / maior) * 100}%` }} />
          </div>
          <span className="sim-chart__values">
            <span className="sim-num">{formatCurrency(linha.a)}</span>
            <span className="sim-num">{formatCurrency(linha.b)}</span>
            <span className={deltaClass(linha.b - linha.a)}>{formatDelta(linha.b - linha.a)}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── Pagina ───────────────────────────────────────────────────────────────── */

export function TaxSimulatorPage(): JSX.Element {
  /* Um item de exemplo para a tela ja abrir com um comparativo montado. E
     default de formulario, nao dado do tenant: a linha e editavel e o texto
     ao lado diz que e exemplo. */
  const [rows, setRows] = useState<ItemRow[]>(() => [novoItem('85171231', '10', '1500')]);
  const [paramsA, setParamsA] = useState<ScenarioParams>(PARAMS_PADRAO);
  const [paramsB, setParamsB] = useState<ScenarioParams>(() => ({
    ...PARAMS_PADRAO,
    date: anoSeguinte(PARAMS_PADRAO.date),
  }));
  const [itemAberto, setItemAberto] = useState<number | null>(null);

  const items = useMemo(() => itensValidos(rows), [rows]);
  const pedidoA = useMemo(() => montarPedido(paramsA, items), [paramsA, items]);
  const pedidoB = useMemo(() => montarPedido(paramsB, items), [paramsB, items]);

  /* O comparativo e a MESMA chamada duas vezes, com dois conjuntos de
     parametros. Nao existe entidade "cenario" no backend, e nao ha por que
     inventar uma: o que distingue A de B sao os parametros da operacao. */
  const queryA = useTaxCalculation(pedidoA);
  const queryB = useTaxCalculation(pedidoB);

  const a = queryA.data;
  const b = queryB.data;

  const atualizaItem = useCallback((key: string, campo: 'ncmCode' | 'quantity' | 'unitValue', valor: string) => {
    setRows((atual) => atual.map((row) => (row.key === key ? { ...row, [campo]: valor } : row)));
  }, []);

  const removeItem = useCallback((key: string) => {
    setRows((atual) => (atual.length === 1 ? atual : atual.filter((row) => row.key !== key)));
  }, []);

  const totalA = a?.totals.totalTax ?? 0;
  const totalB = b?.totals.totalTax ?? 0;
  const aliqA = effectiveRate(a);
  const aliqB = effectiveRate(b);
  const base = baseTotal(a) || baseTotal(b);

  /* So os tributos que algum dos cenarios apurou. */
  const linhas = useMemo<LinhaTributo[]>(() => {
    if (!a && !b) return [];
    return TAX_KEYS.map((key) => ({
      key,
      label: TAX_LABEL[key],
      a: a?.totals[key].value ?? 0,
      b: b?.totals[key].value ?? 0,
    })).filter((linha) => linha.a > 0 || linha.b > 0);
  }, [a, b]);

  /** O tributo que mais mudou — e ele que responde "de onde veio a diferenca". */
  const maiorVariacao = useMemo<LinhaTributo | null>(() => {
    let escolhido: LinhaTributo | null = null;
    for (const linha of linhas) {
      const delta = Math.abs(linha.b - linha.a);
      if (delta < 0.005) continue;
      if (!escolhido || delta > Math.abs(escolhido.b - escolhido.a)) escolhido = linha;
    }
    return escolhido;
  }, [linhas]);

  /** Os dois `itemDetails` casados pelo `index` que o motor devolve. */
  const pares = useMemo(() => {
    const porIndice = new Map<number, { a?: CalculatedItemDetail; b?: CalculatedItemDetail }>();
    for (const item of a?.itemDetails ?? []) {
      porIndice.set(item.index, { ...porIndice.get(item.index), a: item });
    }
    for (const item of b?.itemDetails ?? []) {
      porIndice.set(item.index, { ...porIndice.get(item.index), b: item });
    }
    return [...porIndice.entries()]
      .sort(([x], [y]) => x - y)
      .map(([index, par]) => ({ index, a: par.a, b: par.b }));
  }, [a, b]);

  const erros = [
    ...(a?.errors ?? []).map((mensagem) => ({ cenario: 'A', mensagem })),
    ...(b?.errors ?? []).map((mensagem) => ({ cenario: 'B', mensagem })),
  ];
  const carregando = queryA.isFetching || queryB.isFetching;
  const falha = queryA.error ?? queryB.error;
  const mudancas = diferencas(paramsA, paramsB);

  /* Exporta o comparativo que esta na tela. Nao existe endpoint de relatorio
     de impacto: o CSV e montado com os numeros que o motor ja devolveu, sem
     recalcular nada no navegador. */
  const exportarCsv = useCallback(() => {
    const cabecalho = ['Item', 'NCM', 'CFOP (A)', 'CFOP (B)', 'Base', 'Tributo (A)', 'Tributo (B)', 'Delta'];
    const corpo = pares.map((par) => {
      const tributoA = itemTaxValue(par.a);
      const tributoB = itemTaxValue(par.b);
      const detalhe = par.a ?? par.b;
      return [
        String(par.index + 1),
        detalhe?.ncm ?? '',
        par.a?.cfop ?? '',
        par.b?.cfop ?? '',
        String(detalhe?.totalValue ?? 0),
        String(tributoA),
        String(tributoB),
        String(tributoB - tributoA),
      ];
    });
    const rodape = ['Total', '', '', '', String(base), String(totalA), String(totalB), String(totalB - totalA)];
    const csv = [cabecalho, ...corpo, rodape]
      .map((linha) => linha.map((celula) => `"${celula.replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const url = URL.createObjectURL(new Blob([BOM_UTF8 + csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `comparativo-fiscal-${paramsA.date}-vs-${paramsB.date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [pares, base, totalA, totalB, paramsA.date, paramsB.date]);

  return (
    <div className="sim-page">
      <div className="sim-header">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Simulador de Impacto Fiscal</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Dois cenários sobre os mesmos itens, calculados pelo motor fiscal — as
            regras e as alíquotas são as do seu cadastro, na data de cada cenário.
          </p>
        </div>
        <Button variant="secondary" onClick={exportarCsv} disabled={pares.length === 0}>
          Exportar comparativo (CSV)
        </Button>
      </div>

      <div className="sim-layout">
        {/* ── Parametros ── */}
        <div className="sim-side">
          <Card>
            <div className="sim-card">
              <h3 className="sim-section">Itens</h3>
              <p className="sim-hint">
                Os mesmos itens valem para os dois cenários — o que muda é a
                situação fiscal, não a mercadoria. A linha abaixo vem preenchida
                como exemplo; troque pelos seus itens.
              </p>
              <table className="sim-items">
                <thead>
                  <tr>
                    <th>NCM</th>
                    <th className="sim-th--num">Qtd.</th>
                    <th className="sim-th--num">Valor unit.</th>
                    <th>
                      <span className="sim-sr">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.key}>
                      <td>
                        <Input
                          aria-label="NCM"
                          placeholder="85171231"
                          value={row.ncmCode}
                          onChange={(e) =>
                            atualizaItem(row.key, 'ncmCode', e.target.value.replace(/\D/g, '').slice(0, 8))
                          }
                        />
                      </td>
                      <td>
                        <Input
                          aria-label="Quantidade"
                          type="number"
                          min={0}
                          step="0.001"
                          value={row.quantity}
                          onChange={(e) => atualizaItem(row.key, 'quantity', e.target.value)}
                        />
                      </td>
                      <td>
                        <Input
                          aria-label="Valor unitário"
                          type="number"
                          min={0}
                          step="0.01"
                          placeholder="0,00"
                          value={row.unitValue}
                          onChange={(e) => atualizaItem(row.key, 'unitValue', e.target.value)}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="sim-icon"
                          onClick={() => removeItem(row.key)}
                          disabled={rows.length === 1}
                          aria-label="Remover item"
                          title="Remover item"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Button variant="secondary" size="sm" onClick={() => setRows((atual) => [...atual, novoItem()])}>
                Adicionar item
              </Button>
              {items.length === 0 && (
                <p className="sim-hint sim-hint--warn">
                  Informe NCM (4 dígitos ou mais), quantidade e valor unitário para o
                  motor ter o que calcular.
                </p>
              )}
            </div>
          </Card>

          <Card>
            <div className="sim-card">
              <ScenarioForm
                titulo="Situação de referência"
                etiqueta="A"
                params={paramsA}
                onChange={setParamsA}
                resultado={a}
                carregando={queryA.isFetching}
              />
            </div>
          </Card>

          <Card>
            <div className="sim-card">
              <ScenarioForm
                titulo="Situação proposta"
                etiqueta="B"
                params={paramsB}
                onChange={setParamsB}
                resultado={b}
                carregando={queryB.isFetching}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setParamsB(paramsA)}
                disabled={mudancas.length === 0}
              >
                Igualar ao cenário A
              </Button>
            </div>
          </Card>
        </div>

        {/* ── Resultado ── */}
        <div className="sim-main">
          {falha && (
            <Card>
              <div className="sim-card sim-state">
                <Badge variant="error" size="sm">
                  erro
                </Badge>
                <p className="sim-hint">Não foi possível calcular: {(falha as Error).message}</p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    void queryA.refetch();
                    void queryB.refetch();
                  }}
                >
                  Tentar de novo
                </Button>
              </div>
            </Card>
          )}

          {!falha && items.length === 0 && (
            <Card>
              <div className="sim-card sim-state">
                <p className="sim-muted">
                  Preencha os itens à esquerda. O cálculo roda a cada mudança de
                  parâmetro, nos dois cenários.
                </p>
              </div>
            </Card>
          )}

          {!falha && items.length > 0 && (
            <>
              <div className="sim-kpis">
                <Card>
                  <div className="sim-kpi">
                    <span className="sim-kpi__label">Tributo total — cenário B</span>
                    <strong className="sim-kpi__value">{carregando && !b ? '…' : formatCurrency(totalB)}</strong>
                    <span className={deltaClass(totalB - totalA)}>{formatDelta(totalB - totalA)}</span>
                    <span className="sim-kpi__foot">cenário A: {formatCurrency(totalA)}</span>
                  </div>
                </Card>
                <Card>
                  <div className="sim-kpi">
                    <span className="sim-kpi__label">Alíquota efetiva — cenário B</span>
                    <strong className="sim-kpi__value">{carregando && !b ? '…' : formatPercent(aliqB)}</strong>
                    {/* Diferenca em pontos percentuais: de 17,30% para 18,50% e
                        +1,20 p.p., nao +1,20%. */}
                    <span className={deltaClass(aliqA !== null && aliqB !== null ? aliqB - aliqA : 0)}>
                      {aliqA !== null && aliqB !== null
                        ? `${aliqB - aliqA > 0 ? '+' : ''}${(aliqB - aliqA).toFixed(2).replace('.', ',')} p.p.`
                        : '—'}
                    </span>
                    <span className="sim-kpi__foot">
                      cenário A: {formatPercent(aliqA)} · base {formatCurrency(base)}
                    </span>
                  </div>
                </Card>
                <Card>
                  <div className="sim-kpi">
                    <span className="sim-kpi__label">Maior variação por tributo</span>
                    <strong className="sim-kpi__value">{maiorVariacao ? maiorVariacao.label : '—'}</strong>
                    {maiorVariacao ? (
                      <span className={deltaClass(maiorVariacao.b - maiorVariacao.a)}>
                        {formatDelta(maiorVariacao.b - maiorVariacao.a)}
                      </span>
                    ) : (
                      <span className="sim-delta sim-delta--flat">sem diferença</span>
                    )}
                    <span className="sim-kpi__foot">
                      {maiorVariacao
                        ? `${formatCurrency(maiorVariacao.a)} → ${formatCurrency(maiorVariacao.b)}`
                        : 'os dois cenários produzem o mesmo imposto'}
                    </span>
                  </div>
                </Card>
              </div>

              <Card>
                <div className="sim-card">
                  <h3 className="sim-section">O que muda de A para B</h3>
                  {mudancas.length === 0 ? (
                    <p className="sim-hint sim-hint--warn">
                      Os dois cenários estão com os mesmos parâmetros. O comparativo só
                      tem sentido depois de alterar algo no cenário B — a data (que
                      escolhe a vigência da alíquota e, de 2026 em diante, liga
                      IBS/CBS), a UF de destino ou o regime.
                    </p>
                  ) : (
                    <ul className="sim-diff">
                      {mudancas.map((mudanca) => (
                        <li key={mudanca}>{mudanca}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </Card>

              {erros.length > 0 && (
                <Card>
                  <div className="sim-card">
                    <h3 className="sim-section">Avisos do cálculo</h3>
                    <p className="sim-hint">
                      O motor devolve o que conseguiu apurar mesmo assim. Item sem
                      regra correspondente sai sem tributo — o zero abaixo não é
                      isenção, é ausência de regra cadastrada.
                    </p>
                    <ul className="sim-errors">
                      {erros.map((erro, i) => (
                        <li key={`${erro.cenario}-${i}`}>
                          <span className="sim-errors__tag">{erro.cenario}</span>
                          {erro.mensagem}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              )}

              <Card>
                <div className="sim-card">
                  <h3 className="sim-section">Impacto por tributo</h3>
                  {linhas.length === 0 ? (
                    <p className="sim-muted">
                      {carregando ? 'calculando…' : 'Nenhum tributo foi apurado nos dois cenários.'}
                    </p>
                  ) : (
                    <ImpactChart linhas={linhas} />
                  )}
                </div>
              </Card>

              <Card>
                <div className="sim-card">
                  <h3 className="sim-section">Comparativo por item</h3>
                  <div className="sim-table-wrap">
                    <table className="sim-table">
                      <thead>
                        <tr>
                          <th>NCM</th>
                          <th>CFOP A / B</th>
                          <th className="sim-th--num">Base</th>
                          <th className="sim-th--num">Tributo (A)</th>
                          <th className="sim-th--num">Tributo (B)</th>
                          <th className="sim-th--num">Δ (B−A)</th>
                          <th>
                            <span className="sim-sr">Detalhe</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {pares.map((par) => {
                          const tributoA = itemTaxValue(par.a);
                          const tributoB = itemTaxValue(par.b);
                          const detalhe = par.a ?? par.b;
                          const aberto = itemAberto === par.index;
                          const cfopMudou = Boolean(par.b && par.b.cfop !== par.a?.cfop);
                          return [
                            <tr key={par.index}>
                              <td className="sim-td--mono">
                                {detalhe?.ncm ?? '—'}
                                {detalhe && (
                                  <small className="sim-td__sub">
                                    {detalhe.quantity} × {formatCurrency(detalhe.unitValue)}
                                  </small>
                                )}
                              </td>
                              <td className="sim-td--mono">
                                {par.a?.cfop || '—'}
                                {cfopMudou && par.b ? (
                                  <>
                                    {' / '}
                                    <strong>{par.b.cfop || '—'}</strong>
                                  </>
                                ) : null}
                              </td>
                              <td className="sim-td--num">{formatCurrency(detalhe?.totalValue ?? 0)}</td>
                              <td className="sim-td--num">{formatCurrency(tributoA)}</td>
                              <td className="sim-td--num">{formatCurrency(tributoB)}</td>
                              <td className="sim-td--num">
                                <span className={deltaClass(tributoB - tributoA)}>
                                  {formatDelta(tributoB - tributoA)}
                                </span>
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="sim-icon"
                                  onClick={() => setItemAberto(aberto ? null : par.index)}
                                  aria-expanded={aberto}
                                  aria-label={aberto ? 'Fechar detalhe do item' : 'Abrir detalhe do item'}
                                  title={aberto ? 'Fechar detalhe' : 'Ver tributo por tributo'}
                                >
                                  {aberto ? '−' : '+'}
                                </button>
                              </td>
                            </tr>,
                            aberto ? (
                              <tr key={`${par.index}-detalhe`} className="sim-detail">
                                <td colSpan={7}>
                                  {/* Base, aliquota e CST por tributo: o que o motor
                                      calculou, e o unico jeito de ver onde o delta
                                      nasce dentro de um item. */}
                                  <table className="sim-subtable">
                                    <thead>
                                      <tr>
                                        <th>Tributo</th>
                                        <th className="sim-th--num">Base (A)</th>
                                        <th className="sim-th--num">Alíq. (A)</th>
                                        <th>CST (A)</th>
                                        <th className="sim-th--num">Valor (A)</th>
                                        <th className="sim-th--num">Base (B)</th>
                                        <th className="sim-th--num">Alíq. (B)</th>
                                        <th>CST (B)</th>
                                        <th className="sim-th--num">Valor (B)</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {TAX_KEYS.map((key) => {
                                        const nome = TAX_DETAIL_KEY[key];
                                        const linhaA = par.a?.taxes[nome];
                                        const linhaB = par.b?.taxes[nome];
                                        if (!linhaA && !linhaB) return null;
                                        return (
                                          <tr key={key}>
                                            <td>{TAX_LABEL[key]}</td>
                                            <td className="sim-td--num">
                                              {linhaA ? formatCurrency(linhaA.base) : '—'}
                                            </td>
                                            <td className="sim-td--num">
                                              {linhaA ? formatPercent(linhaA.aliquota) : '—'}
                                            </td>
                                            <td className="sim-td--mono">{linhaA?.cst || '—'}</td>
                                            <td className="sim-td--num">
                                              {linhaA ? formatCurrency(linhaA.value) : '—'}
                                            </td>
                                            <td className="sim-td--num">
                                              {linhaB ? formatCurrency(linhaB.base) : '—'}
                                            </td>
                                            <td className="sim-td--num">
                                              {linhaB ? formatPercent(linhaB.aliquota) : '—'}
                                            </td>
                                            <td className="sim-td--mono">{linhaB?.cst || '—'}</td>
                                            <td className="sim-td--num">
                                              {linhaB ? formatCurrency(linhaB.value) : '—'}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            ) : null,
                          ];
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={2}>Total</td>
                          <td className="sim-td--num">{formatCurrency(base)}</td>
                          <td className="sim-td--num">{formatCurrency(totalA)}</td>
                          <td className="sim-td--num">{formatCurrency(totalB)}</td>
                          <td className="sim-td--num">
                            <span className={deltaClass(totalB - totalA)}>{formatDelta(totalB - totalA)}</span>
                          </td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>

      <style>{`
        .sim-page { padding: 24px; }
        .sim-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; flex-wrap: wrap; margin-bottom: 24px; }
        .sim-layout { display: grid; grid-template-columns: minmax(320px, 400px) minmax(0, 1fr); gap: 16px; align-items: start; }
        @media (max-width: 1100px) { .sim-layout { grid-template-columns: 1fr; } }
        .sim-side { display: flex; flex-direction: column; gap: 16px; }
        .sim-main { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
        .sim-card { padding: 16px; display: flex; flex-direction: column; gap: 12px; align-items: stretch; }
        .sim-section { font-family: Inter, sans-serif; font-size: 20px; line-height: 28px; font-weight: 600; color: #191c1e; margin: 0; }
        .sim-hint { font-size: 12px; line-height: 16px; color: #43474e; margin: 0; }
        .sim-hint--warn { color: #633f0f; background: #ffddba; padding: 8px; border-radius: 0.25rem; }
        .sim-muted { font-size: 12px; color: #74777f; }
        .sim-state { align-items: flex-start; }
        .sim-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

        .sim-items { width: 100%; border-collapse: collapse; }
        .sim-items th { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; font-weight: 500; letter-spacing: 0.02em; text-transform: uppercase; color: #43474e; text-align: left; padding: 0 4px 4px; }
        .sim-items td { padding: 2px 4px; vertical-align: middle; }
        .sim-th--num { text-align: right; }

        .sim-scenario { display: flex; flex-direction: column; gap: 12px; }
        .sim-scenario__head { display: flex; align-items: center; gap: 8px; }
        .sim-scenario__tag { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; font-weight: 500; width: 20px; height: 20px; display: inline-flex; align-items: center; justify-content: center; border-radius: 0.25rem; background: #002045; color: #ffffff; }
        .sim-scenario__title { font-family: Inter, sans-serif; font-size: 20px; line-height: 28px; font-weight: 600; color: #191c1e; margin: 0; }
        .sim-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .sim-rule { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 8px; border-radius: 0.25rem; background: #f2f4f6; }
        .sim-rule__label { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; text-transform: uppercase; letter-spacing: 0.02em; color: #43474e; }
        .sim-rule__name { font-size: 12px; color: #191c1e; }

        .sim-kpis { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
        @media (max-width: 900px) { .sim-kpis { grid-template-columns: 1fr; } }
        .sim-kpi { padding: 16px; display: flex; flex-direction: column; gap: 4px; }
        .sim-kpi__label { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; text-transform: uppercase; letter-spacing: 0.02em; color: #43474e; }
        .sim-kpi__value { font-family: Inter, sans-serif; font-size: 24px; line-height: 32px; font-weight: 600; color: #191c1e; font-variant-numeric: tabular-nums; }
        .sim-kpi__foot { font-size: 12px; color: #74777f; font-variant-numeric: tabular-nums; }

        .sim-delta { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; font-weight: 500; font-variant-numeric: tabular-nums; }
        .sim-delta--up { color: #ba1a1a; }
        .sim-delta--down { color: #1a6b33; }
        .sim-delta--flat { color: #74777f; }

        .sim-diff { margin: 0; padding-left: 18px; font-size: 12px; line-height: 20px; color: #191c1e; }
        .sim-errors { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 4px; }
        .sim-errors li { font-size: 12px; color: #191c1e; display: flex; gap: 8px; align-items: baseline; }
        .sim-errors__tag { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; background: #ffddba; color: #633f0f; border-radius: 0.25rem; padding: 0 6px; }

        .sim-chart { display: flex; flex-direction: column; gap: 8px; }
        .sim-chart__legend { display: flex; gap: 16px; }
        .sim-chart__row--head { align-items: baseline; padding-bottom: 4px; border-bottom: 1px solid #c4c6cf; }
        .sim-chart__col { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; font-weight: 500; letter-spacing: 0.02em; text-transform: uppercase; color: #43474e; }
        .sim-chart__key { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; color: #43474e; }
        .sim-swatch { width: 10px; height: 10px; border-radius: 2px; display: inline-block; }
        .sim-swatch--a { background: #86a0cd; }
        .sim-swatch--b { background: #002045; }
        .sim-chart__row { display: grid; grid-template-columns: 72px minmax(0, 1fr) 280px; gap: 8px; align-items: center; }
        .sim-chart__label { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; color: #191c1e; }
        .sim-chart__bars { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .sim-chart__bar { height: 10px; border-radius: 2px; min-width: 1px; }
        .sim-chart__bar--a { background: #86a0cd; }
        .sim-chart__bar--b { background: #002045; }
        .sim-chart__values { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 4px; text-align: right; }
        .sim-num { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; color: #43474e; font-variant-numeric: tabular-nums; }

        .sim-table-wrap { overflow-x: auto; }
        .sim-table { width: 100%; border-collapse: collapse; }
        .sim-table th { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; font-weight: 500; letter-spacing: 0.02em; text-transform: uppercase; color: #43474e; text-align: left; padding: 8px 12px; border-bottom: 1px solid #c4c6cf; white-space: nowrap; }
        .sim-table td { padding: 8px 12px; border-bottom: 1px solid #eceef0; font-size: 12px; color: #191c1e; vertical-align: middle; }
        .sim-table tfoot td { font-weight: 600; border-top: 1px solid #c4c6cf; border-bottom: none; }
        .sim-td--num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .sim-td--mono { font-family: 'JetBrains Mono', ui-monospace, monospace; white-space: nowrap; }
        .sim-td__sub { display: block; color: #74777f; font-size: 12px; }
        .sim-detail > td { background: #f7f9fb; padding: 8px 12px; }
        .sim-subtable { width: 100%; border-collapse: collapse; }
        .sim-subtable th { padding: 4px 8px; border-bottom: 1px solid #c4c6cf; }
        .sim-subtable td { padding: 4px 8px; border-bottom: 1px solid #e0e3e5; font-size: 12px; }

        .sim-icon { background: none; border: none; cursor: pointer; color: #43474e; font-size: 16px; line-height: 1; padding: 4px 8px; border-radius: 0.25rem; }
        .sim-icon:hover:not(:disabled) { background: #e0e3e5; color: #191c1e; }
        .sim-icon:disabled { color: #c4c6cf; cursor: not-allowed; }
      `}</style>
    </div>
  );
}
